import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Upload, Loader2, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useServices } from "@/lib/hooks";

function useCaptcha() {
  const [captcha, setCaptcha] = useState({ question: "", token: "" });
  const refresh = async () => setCaptcha((await api.get("/captcha")).data);
  useEffect(() => { refresh(); }, []);
  return [captcha, refresh];
}

export default function Quote() {
  const [params] = useSearchParams();
  const { data: services = [] } = useServices();
  const [captcha, refreshCaptcha] = useCaptcha();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  const [form, setForm] = useState({
    full_name: "", phone: "", email: "", address: "", postcode: "",
    service_ids: [], preferred_contact_method: "phone", message: "",
    website: "", captcha_answer: "",
  });

  const preselect = params.get("service");
  useEffect(() => {
    if (preselect && !form.service_ids.includes(preselect)) {
      setForm((f) => ({ ...f, service_ids: [...f.service_ids, preselect] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselect]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (id) =>
    setForm((f) => ({
      ...f,
      service_ids: f.service_ids.includes(id) ? f.service_ids.filter((s) => s !== id) : [...f.service_ids, id],
    }));

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Photo is too large (max 15MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/quote-uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoUrl(data.url);
      toast.success("Photo attached");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone) {
      toast.error("Please provide your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/quote-requests", {
        ...form,
        photo_url: photoUrl,
        captcha_token: captcha.token,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      refreshCaptcha();
      set("captcha_answer", "");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-24 text-center" data-testid="quote-success">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="h-20 w-20 rounded-full bg-green-100 grid place-items-center mx-auto">
          <CheckCircle2 className="h-11 w-11 text-green-600" />
        </motion.div>
        <h1 className="font-display font-black text-slate-900 text-3xl sm:text-4xl mt-6">Request received!</h1>
        <p className="text-slate-500 mt-4 text-lg">Thank you — we'll be in touch shortly with your free, no-obligation quote.</p>
        <button onClick={() => { setSubmitted(false); setForm({ full_name: "", phone: "", email: "", address: "", postcode: "", service_ids: [], preferred_contact_method: "phone", message: "", website: "", captcha_answer: "" }); setPhotoUrl(""); refreshCaptcha(); }} className="mt-8 rounded-full bg-slate-900 px-7 py-3 text-sm font-bold text-white hover:bg-green-700 transition-colors" data-testid="quote-another-btn">
          Submit another request
        </button>
      </div>
    );
  }

  const inputCls = "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 transition";

  return (
    <div data-testid="quote-page">
      <section className="bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14 md:py-16">
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">Get a free quote</h1>
          <p className="mt-4 text-slate-300 text-lg">Tell us about your project. It only takes a minute.</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-12">
        <form onSubmit={submit} className="space-y-6" data-testid="quote-form">
          {/* Honeypot */}
          <input type="text" value={form.website} onChange={(e) => set("website", e.target.value)} name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Full name *</label>
              <input className={inputCls} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="quote-name" placeholder="Jane Smith" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Phone *</label>
              <input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="quote-phone" placeholder="07700 900123" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email (optional)</label>
              <input type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="quote-email" placeholder="jane@email.com" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Postcode</label>
              <input className={inputCls} value={form.postcode} onChange={(e) => set("postcode", e.target.value)} data-testid="quote-postcode" placeholder="M20 2AB" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Address</label>
            <input className={inputCls} value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="quote-address" placeholder="123 Garden Lane" />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Which services are you interested in?</label>
            <div className="grid gap-2.5 sm:grid-cols-2">
              {services.map((s) => (
                <label key={s.id} className={`flex items-center gap-3 rounded-xl border px-4 py-3 cursor-pointer transition ${form.service_ids.includes(s.id) ? "border-green-500 bg-green-50" : "border-slate-200 bg-white hover:border-slate-300"}`} data-testid={`quote-service-${s.id}`}>
                  <input type="checkbox" checked={form.service_ids.includes(s.id)} onChange={() => toggleService(s.id)} className="h-4 w-4 accent-green-600" />
                  <span className="text-sm font-medium text-slate-800">{s.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Preferred contact method</label>
            <div className="flex gap-2">
              {["phone", "email", "text"].map((m) => (
                <button type="button" key={m} onClick={() => set("preferred_contact_method", m)} data-testid={`quote-contact-${m}`} className={`rounded-full px-5 py-2 text-sm font-semibold capitalize transition ${form.preferred_contact_method === m ? "bg-green-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                  {m}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Message</label>
            <textarea rows={4} className={inputCls} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="quote-message" placeholder="Tell us a bit about the job…" />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Attach a photo (optional)</label>
            <label className="flex items-center gap-3 rounded-xl border border-dashed border-slate-300 bg-white px-4 py-4 cursor-pointer hover:border-green-400" data-testid="quote-photo-label">
              {uploading ? <Loader2 className="h-5 w-5 animate-spin text-green-600" /> : <Upload className="h-5 w-5 text-slate-400" />}
              <span className="text-sm text-slate-600">{photoUrl ? "Photo attached ✓ — choose another" : "Click to upload a photo"}</span>
              <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} data-testid="quote-photo-input" />
            </label>
          </div>

          <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Spam check: what is {captcha.question || "…"}?</label>
            <input className={inputCls + " max-w-[160px]"} value={form.captcha_answer} onChange={(e) => set("captcha_answer", e.target.value)} data-testid="quote-captcha" placeholder="Answer" inputMode="numeric" />
          </div>

          <p className="text-xs text-slate-500 leading-relaxed" data-testid="quote-privacy-notice">
            We'll only use your details to respond to your quote request and, if you go ahead, to carry out and invoice the work. We never sell your data. See our <Link to="/privacy" className="text-green-700 underline">Privacy Policy</Link>.
          </p>

          <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-green-600 px-8 py-4 text-base font-bold text-white hover:bg-green-700 transition-colors disabled:opacity-60" data-testid="quote-submit">
            {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Sending…</> : "Send my quote request"}
          </button>
        </form>
      </section>
    </div>
  );
}
